import React, { useEffect, useState } from "react";
import { mergeRight } from "ramda";

import makeStyles from "@mui/styles/makeStyles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import TextField from "@mui/material/TextField";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import DialogContent from "@mui/material/DialogContent";
import { lensProp, pick, set } from "ramda";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import { FetchError } from "../../../util/fetchUtils";
import { useDispatch } from "react-redux";
import { logOut } from "../../../auth/authActions";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import {
  useCreatePapiServer,
  useUpdatePapiServer,
} from "../papiServerServices";

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const useStyles = makeStyles(theme => ({
  checkbox: {
    paddingTop: theme.spacing(1),
    marginLeft: theme.spacing(2),
  },
}));

const initialState = {
  fields: {
    hostname: "",
    port: "",
    secure: false,
  },
};

const EditPapiServer = props => {
  const { server, open, onClose, onSave } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const [fields, setFields] = useState(initialState.fields);
  const [errorAlert, setErrorAlert] = useState({ open: false });

  useEffect(() => {
    if (server && server.patronApiServerId) {
      setFields(pick(["hostname", "port", "secure"], server));
    } else {
      setFields(initialState.fields);
    }
  }, [server]);

  const onFieldChanged = field => (event, optVal) => {
    const value = event.target.value || optVal;
    const update = set(lensProp(field), value, fields);
    setFields(update);
  };

  const showAlert = message =>
    setErrorAlert({
      open: true,
      message,
    });

  const updatePapiServer = useUpdatePapiServer();
  const updateServer = async server => {
    await updatePapiServer.mutateAsync({
      data: server,
      resourceIds: {
        id: server.patronApiServerId,
      },
    });
  };

  const createPapiServer = useCreatePapiServer();
  const createServer = async server => {
    await createPapiServer.mutateAsync({
      data: server,
    });
  };

  const handleError = message => err => {
    if (err instanceof FetchError) {
      if (err.status === 401) {
        dispatch(logOut());
      } else {
        showAlert(message);
      }
    } else {
      showAlert(err.message);
    }
  };

  const handleOnSave = async () => {
    const mergedServer = mergeRight(server, fields);

    try {
      if (mergedServer.patronApiServerId) {
        await updateServer(mergedServer);
      } else {
        await createServer(mergedServer);
      }

      onSave(mergedServer);
    } catch (err) {
      handleError("Unable to save Patron API Server.")(err);
    }
  };

  const handleOnClose = () => {
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      aria-labelledby="form-dialog-title"
    >
      {server.patronApiServerId ? (
        <DialogTitle id="form-dialog-title">
          Patron API Server ({server.patronApiServerId})
        </DialogTitle>
      ) : (
        <DialogTitle id="form-dialog-title">New Patron API Server</DialogTitle>
      )}
      <DialogContent>
        <DialogContentText>
          Specify the Patron API Server Information.
        </DialogContentText>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              id="hostname"
              label="Hostname"
              fullWidth
              value={fields.hostname || ""}
              onChange={onFieldChanged("hostname")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="port"
              label="Port"
              fullWidth
              value={fields.port || ""}
              onChange={onFieldChanged("port")}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormControlLabel
                classes={{ root: classes.checkbox }}
                control={
                  <Checkbox
                    checked={fields.secure}
                    onChange={onFieldChanged("secure")}
                    name="secure"
                    color="primary"
                  />
                }
                label="Secure"
              />
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleOnSave} color="primary">
          Save
        </Button>
      </DialogActions>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
      >
        <Alert
          onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
          severity="error"
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EditPapiServer;
